import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, interval } from 'rxjs';

/**
 * Checks for a newly deployed frontend build and switches the kiosk to it.
 *
 * The Angular service worker downloads the new build in the background.
 * The page only reloads on the home screen with no dialog open, so a
 * customer who is in the middle of ordering is never interrupted.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  /** How often a long-running kiosk asks the server for a new build. */
  private readonly CHECK_INTERVAL_MS = 10 * 60 * 1000;
  /** How often to retry the reload while waiting for an idle home screen. */
  private readonly IDLE_RETRY_MS = 15 * 1000;

  private updateReady = false;
  private reloading = false;

  constructor(
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private router: Router,
    private dialog: MatDialog,
    private ngZone: NgZone
  ) { }

  start(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(event => {
        console.log('New frontend version downloaded:', event.latestVersion.hash);
        this.updateReady = true;
        this.reloadWhenIdle();
      });

    // The cached build can no longer be served (for example, files were
    // removed from the server). Reloading fetches the current build.
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Service worker unrecoverable state:', event.reason);
      this.reload();
    });

    // Re-check when the kiosk returns to the home screen.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.reloadWhenIdle());

    // Start polling only after the app is stable, otherwise the interval
    // keeps Angular from ever becoming stable.
    const appIsStable$ = this.appRef.isStable.pipe(first(stable => stable));
    this.ngZone.runOutsideAngular(() => {
      concat(appIsStable$, interval(this.CHECK_INTERVAL_MS)).subscribe(() => this.checkNow());
      interval(this.IDLE_RETRY_MS).subscribe(() => this.ngZone.run(() => this.reloadWhenIdle()));
    });
  }

  /** Asks the server whether a new build exists. Resolves true when one was found. */
  async checkNow(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }
    try {
      return await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.warn('Failed to check for a new frontend version:', error);
      return false;
    }
  }

  private reloadWhenIdle(): void {
    if (!this.updateReady || this.reloading) {
      return;
    }
    const onHome = this.router.url === '/' || this.router.url.startsWith('/home');
    if (!onHome || this.dialog.openDialogs.length > 0) {
      return;
    }
    this.swUpdate.activateUpdate()
      .catch(error => console.warn('Failed to activate the new frontend version:', error))
      .finally(() => this.reload());
  }

  private reload(): void {
    if (this.reloading) {
      return;
    }
    this.reloading = true;
    document.location.reload();
  }
}
